"use client";

import { Scissors, Ruler, Anchor, Feather, Layers, Hand } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  label: "DIAGNOSTIC No. 01",
  title: "Sleep Apnea Evaluation",
  description: "Evaluation of snoring, witnessed breathing pauses, daytime sleepiness, and other symptoms that may suggest sleep apnea.",
  icon: Layers,
  colStart: "lg:col-start-1",
  offset: "lg:mt-0"
}, {
  label: "DIAGNOSTIC No. 02",
  title: "CPAP & BiPAP Follow-Up",
  description: "Review of CPAP or BiPAP use, comfort issues, pressure concerns, and treatment response.",
  icon: Scissors,
  colStart: "lg:col-start-5",
  offset: "lg:mt-20"
}, {
  label: "DIAGNOSTIC No. 03",
  title: "CPAP Data Review",
  description: "Review of available CPAP or BiPAP data when available, including usage patterns, mask leak, pressure tolerance, and treatment response.",
  icon: Feather,
  colStart: "lg:col-start-9",
  offset: "lg:-mt-10"
}, {
  label: "DIAGNOSTIC No. 04",
  title: "Mask Comfort Support",
  description: "Guidance for patients experiencing mask discomfort, air leaks, dryness, or difficulty tolerating PAP therapy.",
  icon: Ruler,
  colStart: "lg:col-start-1",
  offset: "lg:mt-12"
}, {
  label: "DIAGNOSTIC No. 05",
  title: "Oxygen Therapy Follow-Up",
  description: "Follow-up care for patients using oxygen therapy or needing monitoring related to oxygen needs.",
  icon: Anchor,
  colStart: "lg:col-start-5",
  offset: "lg:mt-28"
}, {
  label: "DIAGNOSTIC No. 06",
  title: "Sleep Concerns & Fatigue",
  description: "Evaluation of sleep-related symptoms such as poor sleep quality, fatigue, or ongoing daytime tiredness.",
  icon: Hand,
  colStart: "lg:col-start-9",
  offset: "lg:mt-4"
}];
export default function SleepEvaluationCpapCare() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4812" id="sleep-services" className="relative w-full overflow-hidden bg-background py-32 md:py-48">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[0.75rem] tracking-[0.15em] uppercase mb-10 text-teal-muted">
            Sleep Medicine — Clinical Precision
          </p>
          <h2 className="font-default text-5xl md:text-6xl lg:text-[4rem] leading-[1.1] tracking-tight text-primary">
            Sleep Apnea & CPAP Follow-Up
          </h2>
          <p className="font-default mt-10 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto text-slate-text">
            We evaluate sleep-related breathing concerns and provide ongoing support for sleep apnea treatment, CPAP use, and related symptoms.
          </p>
        </div>

        {}
        <div className="relative mt-28 md:mt-40 grid grid-cols-1 lg:grid-cols-12 gap-y-20 lg:gap-y-32 gap-x-8">
          {features.map((feature, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.25
          });
          const Icon = feature.icon;
          return <div data-index={i} ref={reveal.ref} key={i} className={`group lg:col-span-4 ${feature.colStart} ${feature.offset} transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${i % 3 * 120}ms`
          }}>
                <div className="pt-8 border-t border-slate-200">
                  <p className="font-mono text-[0.75rem] tracking-[0.15em] uppercase mb-8 text-teal-muted">
                    {feature.label}
                  </p>

                  <div className="mb-10 transition-transform duration-500 ease-out group-hover:-translate-y-1 text-primary">
                    <div className="inline-flex items-center justify-center h-24 w-24 rounded-lg bg-secondary">
                      <Icon className="h-12 w-12 transition-all duration-700 group-hover:opacity-100 opacity-80" strokeWidth={1.5} style={{
                    strokeDasharray: 200,
                    strokeDashoffset: reveal.isVisible ? 0 : 200,
                    transition: "stroke-dashoffset 1.2s ease-out, opacity 0.5s ease-out"
                  }} />
                    </div>
                  </div>

                  <h3 className="font-default font-semibold text-2xl md:text-[1.75rem] leading-tight mb-5 transition-colors duration-300 text-primary">
                    {feature.title}
                  </h3>

                  <p className="font-default text-base md:text-lg leading-relaxed text-slate-text">
                    {feature.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={quote.ref} className={`mt-32 md:mt-48 grid grid-cols-12 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <blockquote className="col-span-12 lg:col-start-3 lg:col-span-8 text-center">
            <p className="font-serif italic text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.25] text-primary">
              &ldquo;Quality sleep and healthy breathing are closely connected. Our goal is to help patients understand their symptoms, review treatment options, and move forward with a clear care plan.&rdquo;
            </p>
          </blockquote>
        </div>
      </div>
    </section>;
}